"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Bell, ChevronDown, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { goToDashboard } from "@/features/sso/goToDashboard";
import { useAuth } from "@/features/auth/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { getDateLocale, getLuxonLocale } from "@/lib/i18n/dateLocales";
import { useCalendar } from "@/contexts/CalendarContext";
import { useSelectedStaff } from "@/contexts/SelectedStaffContext";
import { useStaffScheduleByDate } from "@/hooks/calendar/useStaffScheduleByDate";
import { DateTime } from "luxon";
import { useCompanySettingsInfo } from "@/hooks/shared/useCompanySettingsInfo";
import { useAccessPolicy } from "@/hooks/access/useAccess";
import type { Policy } from "@/lib/auth/AccessPolicy";
import { Guard } from "@/components/access/Guard";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchDayReport } from "@/hooks/company/useDayReport";
import { salonNow } from "@/lib/time";
import { useNotificationsPanel } from "@/contexts/NotificationsContext";
import { useNotifications } from "@/hooks/calendar/useNotifications";
import { useWaitlistMatches } from "@/hooks/calendar/useWaitlistMatches";
import { useSidebar } from "@/contexts/SidebarContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LANGUAGE_OPTIONS,
  MAIN_LANGUAGE_STORAGE_KEY,
  changeLanguageAndPersist,
} from "@/lib/i18n/languageSettings";
// Avatar for footer
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.resolvedLanguage || i18n.language)?.slice(0, 2).toUpperCase() || "EN";

  const handleLanguageChange = (langCode: string) => {
    void changeLanguageAndPersist(i18n, langCode, MAIN_LANGUAGE_STORAGE_KEY);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent hover:text-accent-foreground text-sm font-medium"
          aria-label={t("sidebar.change_language", "Change language")}
        >
          <i className="bi-globe text-[#6B5E4C] text-lg" />
          <span className="text-[#6B5E4C]">{currentLang}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="w-40 max-h-64 overflow-y-auto">
        {LANGUAGE_OPTIONS.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={i18n.language?.startsWith(lang.code) ? "bg-accent" : ""}
          >
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// The mini calendar's day highlights: classes on the day cell that style its round button; the
// focus variants keep them while a just-clicked day holds focus
const DAY_TODAY =
  "[&>button]:bg-amber-400 [&>button]:text-white [&>button]:hover:bg-amber-400 [&>button]:hover:text-white [&>button]:focus:bg-amber-400 [&>button]:focus:text-white";
const DAY_SELECTED =
  "[&>button]:bg-sky-100 [&>button]:text-sky-700 [&>button]:ring-1 [&>button]:ring-sky-400 [&>button]:hover:bg-sky-100 [&>button]:hover:text-sky-700 [&>button]:focus:bg-sky-100 [&>button]:focus:text-sky-700";

/** A picker day as YYYY-MM-DD in the browser's calendar — how the picker itself tells days apart. */
export function dayIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The one highlight a mini-calendar day gets. Exactly one, so two fills never fight over the same
 * button: today that was also the selected day used to show white digits on pale blue.
 */
export function dayHighlight(
  day: string,
  today: string,
  selected: string,
  inWeek: boolean,
): "today" | "today-selected" | "selected" | "week" | null {
  if (day === today) return day === selected ? "today-selected" : "today";
  if (day === selected) return "selected";
  return inWeek ? "week" : null;
}

// The picture the API sends when a salon has no logo of its own (boona's ImageKind.COMPANY_IMAGE
// fallback); the dashboard's sidebar shows the name's first letter instead, and so does this one.
const STOCK_LOGO = "/company/no-company.png";

/** The salon's own logo, or null when it has none (the API's stock picture counts as none). */
export function uploadedLogo(logo?: string | null): string | null {
  const url = (logo || "").trim();
  return url && !url.split("?")[0].endsWith(STOCK_LOGO) ? url : null;
}

/**
 * The salon's logo and name and, for those who get them, its alerts — like the dashboard's sidebar:
 * the uploaded logo, else the name's first letter on a tile (also when the logo fails to load). The
 * alerts keep their own corner on the right, side by side; a long name is cut with an ellipsis
 * instead of pushing them around.
 */
export function SidebarHeader({ title, logo, alerts }: { title: string; logo?: string | null; alerts?: ReactNode }) {
  const [failed, setFailed] = useState<string | null>(null);
  const src = uploadedLogo(logo);
  return (
    <div className="flex items-center gap-2 p-4">
      {src && src !== failed ? (
        <img
          src={src}
          alt=""
          onError={() => setFailed(src)}
          className="h-8 w-8 shrink-0 rounded-lg border border-[#e9ecef] bg-white object-cover"
        />
      ) : (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-2 border-[#bbdefb] bg-[#e3f2fd] text-base font-bold text-[#1976d2]">
          {(Array.from(title.trim())[0] || "C").toUpperCase()}
        </div>
      )}
      <span className="min-w-0 flex-1 truncate font-medium" title={title}>{title}</span>
      {alerts}
    </div>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { currentDate, setDate } = useCalendar();
  const { selectedStaffId } = useSelectedStaff();
  const { data: companySettings } = useCompanySettingsInfo();
  const policy = useAccessPolicy();
  const queryClient = useQueryClient();
  const { toggle: toggleNotifications, openTo } = useNotificationsPanel();
  const { setSidebarOpen } = useSidebar();
  const { data: notifications = [] } = useNotifications(policy.notifications.access);
  const notificationCount = notifications.length;
  // Always-on poll: drives the match toasts (the single toast-firing
  // instance) AND the waitlist icon badge. Gated by notifications access.
  const { data: waitlistData } = useWaitlistMatches(
    policy.notifications.access, { withToasts: true });
  const waitlistCount = waitlistData?.matches?.length ?? 0;
  const { t, i18n } = useTranslation();

  const handleOpenWaitlist = () => {
    setSidebarOpen(false);
    openTo("waitlist");
  };

  // Dynamic date-fns locale based on current i18n language
  const dateLocale = useMemo(() => getDateLocale(i18n.language), [i18n.language]);
  const luxonLocale = useMemo(() => getLuxonLocale(i18n.language), [i18n.language]);

  // Handler that closes mobile sidebar before opening notifications panel
  const handleOpenNotifications = () => {
    setSidebarOpen(false); // Close mobile sidebar first
    toggleNotifications();
  };
  // Use effective staff id (RBAC forced id overrides UI selection)
  const effectiveStaffId = policy.timetable.forcedStaffId ?? selectedStaffId;

  // ──────────────────────────────────────────────────────────────────────────
  //  Dynamic availability & disabled‑day logic
  // ──────────────────────────────────────────────────────────────────────────
  const { data: schedule } = useStaffScheduleByDate(currentDate);

  const availableDates = useMemo(() => {
    if (!effectiveStaffId || !schedule) return null;
    return new Set(
      Object.entries(schedule)
        .filter(([, ids]) => Array.isArray(ids) && ids.includes(effectiveStaffId))
        .map(([date]) => date)
    );
  }, [effectiveStaffId, schedule]);

  //  ⏰  Compute disabled days for *the month currently displayed* (no hard‑coding)
  const disabledDays = useMemo(() => {
    const start = currentDate.startOf("month");
    const end = currentDate.endOf("month");

    const days: Date[] = [];
    for (let d = start; d <= end; d = d.plus({ days: 1 })) {
      const isUnavailableByStaff =
        !!selectedStaffId && !!availableDates && !availableDates.has(d.toISODate());
      // NOTE: allow selecting any past day; keep only staff unavailability disabled
      if (isUnavailableByStaff) {
        days.push(d.toJSDate());
      }
    }
    return days.length ? days : undefined;
  }, [selectedStaffId, availableDates, currentDate]);

  // ──────────────────────────────────────────────────────────────────────────
  //  Week‑row highlight when a staff member is selected
  // ──────────────────────────────────────────────────────────────────────────
  const weekDates = useMemo(() => {
    const monday = currentDate.minus({ days: (currentDate.weekday + 6) % 7 });
    return Array.from({ length: 7 }, (_, i) => monday.plus({ days: i }).toISODate());
  }, [currentDate]);

  const weekSelectedModifier = useMemo(() => {
    return (date: Date) => {
      if (!effectiveStaffId) return false;
      const iso = DateTime.fromJSDate(date).toISODate();
      return weekDates.includes(iso);
    };
  }, [effectiveStaffId, weekDates]);

  // One highlight per day (dayHighlight): today, the selected day and the master's week
  const todayIso = dayIso(new Date());
  const selectedIso = dayIso(currentDate.toJSDate());
  const highlightOf = (date: Date) =>
    dayHighlight(dayIso(date), todayIso, selectedIso, weekSelectedModifier(date));

  // ──────────────────────────────────────────────────────────────────────────
  //  Render
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="w-52 flex flex-col h-full bg-white">
      {/* Brand header */}
      <SidebarHeader
        title={companySettings?.title || "Unknown"}
        logo={companySettings?.logo}
        alerts={
        <Guard check={(p) => p.notifications.access} mode="hide">
          <TooltipProvider>
            <div className="flex shrink-0 items-center gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleOpenNotifications}
                    className="relative cursor-pointer hover:opacity-80 transition-opacity"
                    aria-label={t("sidebar.notifications", "Notifications")}
                  >
                    <Bell size={16} className="text-red-500" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center">
                        {notificationCount > 9 ? "9+" : notificationCount}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{t("sidebar.notifications", "Notifications")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Waiting list — consolidated into the same sliding panel;
                  jumps straight to its tab (waitlist README §4.6 rewire) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleOpenWaitlist}
                    className="relative cursor-pointer hover:opacity-80 transition-opacity"
                    aria-label={t("waitlist.title", "Waiting list")}
                  >
                    <Hourglass size={16} className="text-emerald-600" />
                    {waitlistCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-3 min-w-3 items-center justify-center rounded-full bg-emerald-600 px-0.5 text-[8px] text-white">
                        {waitlistCount > 9 ? "9+" : waitlistCount}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{t("waitlist.title", "Waiting list")}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </Guard>
        }
      />

      {/* Mini calendar */}
      <div className="overflow-hidden">
        <div className="flex items-center justify-between p-4 pb-1">

          <div className="flex">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setDate(currentDate.minus({ months: 1 } as any))}
            >
              <ChevronDown className="h-4 w-4 rotate-90" />
            </Button>
          </div>
          <h2 className="font-medium">{currentDate.setLocale(luxonLocale).toFormat("LLLL yyyy")}</h2>
          <div className="flex">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setDate(currentDate.plus({ months: 1 }).startOf("month"))}
            >
              <ChevronDown className="h-4 w-4 -rotate-90" />
            </Button>
          </div>
        </div>

        <div className="calendar-container overflow-hidden px-2">
          {/* No transform scale on the calendar: a fractional scale renders
              day numbers off the pixel grid (blurry). Each column is an equal
              1/7 fraction of the full width (flex-1) with the day circle
              centered in it, so the grid fills and spreads evenly (air
              between dates) instead of packing tight to the left. */}
          <Calendar
            mode="single"
            selected={currentDate.toJSDate()}
            onSelect={(date) => {
              if (!date) return;
              const dt = DateTime.fromJSDate(date);
              // Update current date in calendar view
              setDate(dt);
              // If user clicked a *past* day (salon TZ), prefetch its day report
              try {
                const today = salonNow().startOf("day");
                if (dt.startOf("day") < today) {
                  const iso = dt.toISODate();
                  // Prime React Query cache via hook-owned helper
                  prefetchDayReport(queryClient, iso);
                }
              } catch (e) {
                // Non-fatal: selection should still work even if prefetch fails
                console.warn("[Sidebar] Failed to prefetch day report", e);
              }
            }}
            locale={dateLocale}
            month={currentDate.toJSDate()}
            defaultMonth={currentDate.toJSDate()}
            showOutsideDays
            fixedWeeks
            disabled={disabledDays}
            modifiers={{
              todayPlain: (date) => highlightOf(date) === "today",
              todaySelected: (date) => highlightOf(date) === "today-selected",
              selectedDay: (date) => highlightOf(date) === "selected",
              weekSelected: (date) => highlightOf(date) === "week",
            }}
            className="w-full -mt-1"
            classNames={{
              months: "flex flex-col space-y-4",
              month: "space-y-2",
              month_caption: "hidden",
              caption_label: "sr-only",
              nav: "hidden",
              month_grid: "w-full border-collapse",
              weekdays: "flex w-full",
              weekday: "flex-1 text-center text-muted-foreground font-normal text-[0.62rem]",
              week: "flex w-full mt-1.5",
              day: "flex-1 flex h-7 items-center justify-center relative p-0 focus-within:relative focus-within:z-20",
              // h-7 w-6 (taller than wide) + rounded-full → an oval highlight
              // (matches the prior design) rather than a perfect circle.
              day_button: "inline-flex h-7 w-6 items-center justify-center rounded-full p-0 text-[0.75rem] font-normal antialiased subpixel-antialiased hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
              range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
              hidden: "invisible",
              // the wrapper's own selected/today fills would fight the highlights below
              selected: "",
              today: "",
            }}
            modifiersClassNames={{
              // today = amber fill, manually selected = light-blue ring (matches
              // the prior design); both target the round day button. Today that is
              // also the selected day keeps the amber and gets the ring around it.
              todayPlain: DAY_TODAY,
              todaySelected: `${DAY_TODAY} [&>button]:ring-2 [&>button]:ring-sky-400 [&>button]:ring-offset-1`,
              selectedDay: DAY_SELECTED,
              disabled: "text-muted-foreground opacity-40",
              outside: "text-muted-foreground opacity-40",
              weekSelected: "[&>button]:bg-amber-100 [&>button]:text-amber-700",
            }}
          />
        </div>
      </div>

      <SidebarMenu policy={policy} />
      <SidebarFooter user={user} onLogout={logout} />
    </div>
  );
}

type MenuItem = { icon: string; label: string; href: string; visible: boolean };

/**
 * The menu that scrolls under the mini calendar — two variants, because owners and admins work in
 * the dashboard while a master only needs her own pages. A login with dashboard access gets the
 * dashboard's sections; any other login (a master) gets My calendar (boona
 * docs/codebase/google-calendar/README.md §11) and Email notifications (notify-prefs/masters.md §5),
 * dashboard pages that need no access right. Admins reach those two from the dashboard's settings.
 */
export function SidebarMenu({ policy }: { policy: Policy }) {
  const { t } = useTranslation();
  const items: MenuItem[] = policy.dashboard.access
    ? [
        { icon: "bi-people", label: t("sidebar.nav.staff", "Staff"), href: "/dashboard/staff", visible: policy.sidebar.staff },
        // the team's work schedule (boona docs/codebase/team-schedule/), the same right as Staff
        { icon: "bi-calendar3-week", label: t("sidebar.nav.work_schedule", "Work schedule"), href: "/dashboard/staff/schedule", visible: policy.sidebar.staff },
        { icon: "bi-journal-text", label: t("sidebar.nav.records", "Records"), href: "/dashboard/records", visible: policy.sidebar.records },
        { icon: "bi-grid", label: t("sidebar.nav.services", "Services"), href: "/dashboard/services", visible: policy.sidebar.services },
        { icon: "bi-box-seam", label: t("sidebar.nav.storage", "Stock"), href: "/dashboard/storage", visible: policy.sidebar.storage },
        { icon: "bi-basket", label: t("sidebar.nav.goods", "Goods"), href: "/dashboard/goods", visible: policy.sidebar.goods },
        { icon: "bi-puzzle", label: t("sidebar.nav.tech_cards", "Tech Cards"), href: "/dashboard/tech-cards", visible: policy.sidebar.techCards },
        { icon: "bi-currency-dollar", label: t("sidebar.nav.financials", "Financials"), href: "/dashboard/financials", visible: policy.sidebar.financials },
        { icon: "bi-people-fill", label: t("sidebar.nav.clients", "Clients"), href: "/dashboard/clients", visible: policy.sidebar.clients },
        { icon: "bi-gear", label: t("sidebar.nav.settings", "Settings"), href: "/dashboard/", visible: policy.sidebar.settings },
        { icon: "bi-star", label: t("sidebar.nav.reviews", "Reviews"), href: "/dashboard/reviews", visible: policy.sidebar.reviews },
      ]
    : [
        { icon: "bi-calendar-check", label: t("sidebar.my_calendar", "My calendar"), href: "/dashboard/settings/calendar", visible: true },
        { icon: "bi-envelope", label: t("sidebar.email_notifications", "Email notifications"), href: "/dashboard/settings/notifications", visible: true },
      ];
  return (
    <div className="flex-1 overflow-auto px-3">
      <ul className="flex w-full min-w-0 flex-col gap-1">
        {items.map((item) => (
          <li key={item.href} className="relative">
            {item.visible ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  // Route ALL dashboard hops through SSO cookie exchange
                  void goToDashboard(item.href);
                }}
                className="flex items-center text-[14px] gap-3 px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground w-full text-left"
              >
                <i className={`${item.icon} text-[#6B5E4C] text-lg`} />
                <span className="text-[#6B5E4C]">{item.label}</span>
              </button>
            ) : (
              // Shown greyed, not a link: people see what the salon can do and
              // can ask the owner for it (same rule as the dashboard sidebar)
              <span
                aria-disabled="true"
                title={t("sidebar.nav.no_access", "Ask the salon owner for access")}
                className="flex items-center text-[14px] gap-3 px-3 py-2 rounded-md w-full cursor-not-allowed opacity-50 select-none"
              >
                <i className={`${item.icon} text-[#979a9d] text-lg`} />
                <span className="text-[#979a9d]">{item.label}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Pinned under the menu, one row: who is signed in (a tap logs out) and the language. Nothing
 * else lives here, so on a phone the menu above keeps the height it needs.
 */
export function SidebarFooter({
  user,
  onLogout,
}: {
  user?: { name?: string | null; email?: string | null } | null;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const logOut = t("widget.client.logout", "Log out");
  return (
    <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
      <button
        type="button"
        onClick={onLogout}
        aria-label={logOut}
        title={logOut}
        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent hover:text-accent-foreground"
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src="/placeholder-user.jpg" />
          <AvatarFallback>
            {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <i className="bi-box-arrow-right text-[#6B5E4C] text-lg" />
      </button>
      <LanguageSelector />
    </div>
  );
}
